import { Capacitor } from "@capacitor/core";
import type {
	CustomerInfo as NativeCustomerInfo,
	PurchasesOfferings as NativeOfferings,
	PurchasesPackage as NativePackage,
} from "@revenuecat/purchases-capacitor";
import type {
	CustomerInfo as WebCustomerInfo,
	Offerings as WebOfferings,
	Package as WebPackage,
} from "@revenuecat/purchases-js";
import * as NativeService from "./revenueCatNativeService";
import * as WebService from "./revenueCatWebService";

/**
 * RevenueCat Service (Platform Delegator)
 * Delegates to platform-specific services based on runtime platform
 * - Web: Uses revenueCatWebService (Stripe)
 * - Native: Uses revenueCatNativeService (App Store/Play Store)
 */

// Type aliases to handle both SDKs
export type CustomerInfo = NativeCustomerInfo | WebCustomerInfo;
export type PurchasesOfferings = NativeOfferings | WebOfferings;
export type PurchasesPackage = NativePackage | WebPackage;

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Initialize RevenueCat SDK
 * Automatically selects the correct SDK based on platform
 */
export async function configure(userId?: string): Promise<void> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			if (!userId) {
				console.warn(
					"[RevenueCat] Web SDK requires user ID - skipping configuration",
				);
				return;
			}
			await WebService.configure(userId);
		} else {
			await NativeService.configure(platform, userId);
		}
	} catch (error) {
		console.error("[RevenueCat] Configuration failed:", error);
		throw error;
	}
}

// ============================================================================
// OFFERINGS & PRODUCTS
// ============================================================================

/**
 * Fetch available offerings (subscription plans)
 */
export async function getOfferings(): Promise<PurchasesOfferings | null> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			return (await WebService.getOfferings()) as unknown as PurchasesOfferings;
		}

		return await NativeService.getOfferings();
	} catch (error) {
		console.error("[RevenueCat] Failed to fetch offerings:", error);
		return null;
	}
}

// ============================================================================
// PURCHASES
// ============================================================================

/**
 * Purchase a package
 */
export async function purchasePackage(
	packageToPurchase: PurchasesPackage,
): Promise<{ customerInfo: CustomerInfo; success: boolean }> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			return (await WebService.purchasePackage(
				packageToPurchase as unknown as WebPackage,
			)) as unknown as { customerInfo: CustomerInfo; success: boolean };
		}

		return await NativeService.purchasePackage(
			packageToPurchase as NativePackage,
		);
	} catch (error) {
		console.error("[RevenueCat] Purchase failed:", error);
		throw error;
	}
}

// ============================================================================
// CUSTOMER INFO & SUBSCRIPTIONS
// ============================================================================

/**
 * Get customer info (current subscription status)
 */
export async function getCustomerInfo(): Promise<CustomerInfo> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			return (await WebService.getCustomerInfo()) as unknown as CustomerInfo;
		}

		return await NativeService.getCustomerInfo();
	} catch (error) {
		console.error("[RevenueCat] Failed to get customer info:", error);
		throw error;
	}
}

/**
 * Check if user has active subscription
 */
export async function hasActiveSubscription(): Promise<boolean> {
	const platform = Capacitor.getPlatform();

	if (platform === "web") {
		return await WebService.hasActiveSubscription();
	}

	return await NativeService.hasActiveSubscription();
}

/**
 * Restore previous purchases (Native only)
 */
export async function restorePurchases(): Promise<CustomerInfo> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			// Web SDK doesn't have restore - just get current customer info
			return await getCustomerInfo();
		}

		return await NativeService.restorePurchases();
	} catch (error) {
		console.error("[RevenueCat] Failed to restore purchases:", error);
		throw error;
	}
}

// ============================================================================
// USER MANAGEMENT
// ============================================================================

/**
 * Set user ID (useful when user logs in after SDK initialization)
 * Note: Web SDK sets user ID at configuration time only
 */
export async function setUserId(userId: string): Promise<void> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			// Web SDK user ID is set at configure time only
			return;
		}

		await NativeService.setUserId(userId);
	} catch (error) {
		console.error("[RevenueCat] Failed to set user ID:", error);
		throw error;
	}
}

/**
 * Log out user
 */
export async function logOut(): Promise<void> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			WebService.reset();
			return;
		}

		await NativeService.logOut();
	} catch (error) {
		console.error("[RevenueCat] Failed to log out:", error);
		throw error;
	}
}

/**
 * Get subscription management URL
 * Opens the platform-specific subscription management page where users can cancel
 * - iOS: App Store subscriptions
 * - Android: Play Store subscriptions
 * - Web: Stripe billing portal
 */
export async function getManagementURL(): Promise<string | null> {
	try {
		const platform = Capacitor.getPlatform();

		if (platform === "web") {
			return await WebService.getManagementURL();
		}

		return await NativeService.getManagementURL();
	} catch (error) {
		console.error("[RevenueCat] Failed to get management URL:", error);
		return null;
	}
}
